// src/components/DeletionReasonModal.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, AlertTriangle, MessageSquare, User, Calendar, Clock, Users } from 'lucide-react';
import { Button } from './ui/button';
import { format, parseISO } from 'date-fns';

export default function DeletionReasonModal({ reservation, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      setError('Please provide a reason for deletion');
      return;
    }
    
    onConfirm(reason.trim());
  };

  const handleCancel = (e) => {
    e.preventDefault(); // Prevent any default behavior
    e.stopPropagation(); // Stop event bubbling
    onClose(); // Call the onClose function
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose} // This closes modal when clicking backdrop
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking modal content
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
              <h2 className="text-lg sm:text-xl font-bold">Confirm Deletion</h2>
            </div>
            <button
              onClick={handleCancel} // Use handleCancel instead of onClose directly
              className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6">
          {/* Reservation Summary */}
          <div className="bg-[#F9F9F9] rounded-lg p-3 sm:p-4 mb-4 border border-[#E5E5E5]">
            <h3 className="text-sm font-medium text-[#2D2D2D] mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-[#8B2635]" />
              {reservation.customer_name}
            </h3>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex items-center gap-2 text-[#6B6B6B]">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{formatDate(reservation.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B6B6B]">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{reservation.time}</span>
              </div>
              <div className="flex items-center gap-2 text-[#6B6B6B]">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{reservation.guests} guests</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#2D2D2D] mb-2">
                Deletion Reason <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#6B6B6B]" />
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter the reason for deleting this reservation..."
                  className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 min-h-[100px] ${
                    error ? 'border-red-300 focus:ring-red-200' : 'border-[#E5E5E5] focus:ring-[#8B2635]/40 focus:border-[#8B2635]'
                  }`}
                  autoFocus
                  onClick={(e) => e.stopPropagation()} // Ensure click on textarea doesn't bubble
                />
              </div>
              {error && (
                <p className="text-xs text-red-500 mt-1">{error}</p>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  This action will move the reservation to the deleted records archive. 
                  You can restore it later if needed.
                </span>
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel} // Use handleCancel instead of onClose directly
                className="flex-1 border-[#E5E5E5] text-[#6B6B6B] hover:bg-[#F5F5F5]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                Confirm Deletion
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}